/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from "react";
import { TradeLeg } from "../../../components/TradeLeg/TradeLeg";
import { GlobalStore } from "../../../store/global/GlobalStore";
import {
  TokenSelectModal,
  TokenSelectMode,
} from "../../../components/Modal/instance/SelectTokenModal/TokenSelectModal";
import { SelectTokensButton } from "./SelectTokensButton";
import { ReverseTokensButton } from "../../../components/ReverseTokens/ReverseTokensButton";
import { Amount } from "../../../types/amount";
import { SplitEvenlyButton } from "./SplitEvenlyButton";
import percentRound from "percent-round";
import { fill, isEqual } from "lodash-es";
import { ToggleButton } from "../../../components/ToggleButton/ToggleButton";
import { SwapAndSend } from "../../../components/SwapAndSendSection/SwapAndSend";
import { INITIAL_RECEIVER_STATE } from "../../../utils/swapAndSendUtils";
import { useDisplayOnClick } from "../../../hooks/useDisplayOnClick";
import { MakerSectionHeader } from "../elements/MakerSectionHeader";

export interface MakerSectionProps {}

export const MakerSection: React.FC<MakerSectionProps> = props => {
  const makerTokens = GlobalStore.useState(s => s.makerTokens);
  const takerTokens = GlobalStore.useState(s => s.takerTokens);
  const wrapQuote = GlobalStore.useState(s => s.wrapQuote);
  const tokenSelectMode: TokenSelectMode = takerTokens.length > 1 ? "single" : "multiple";
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isSending = GlobalStore.useState(s => s.isSending);
  const selectTokenButtonVisible = makerTokens.length > 0 && takerTokens.length <= 1;
  const { onClick, isDisplayed, animationState, forceOpen } = useDisplayOnClick({
    duration: 250,
  });

  const getRoundedPcts = useCallback(() => {
    // Create a new array and fill it with even values.
    const newEvenPercentages = fill(Array(makerTokens.length), 100 / makerTokens.length);
    // Round 33.3,33.3,33.3 to 34,33,33
    const roundedPercentages = percentRound(newEvenPercentages);
    return roundedPercentages;
  }, [makerTokens]);

  const switchTradeTokens = () => {
    GlobalStore.update(s => {
      const makerTokens = [...s.makerTokens];
      const takerTokens = [...s.takerTokens];
      s.makerTokens = takerTokens.map(token => {
        return {
          ...token,
          pct: 100 / takerTokens.length,
          isLocked: false,
          amount: Amount.ZERO,
        };
      });
      s.takerTokens = makerTokens;
      // Clear quote if there was one
      s.quote = undefined;
    });
  };

  const handleToggle = useCallback(() => {
    onClick();
    GlobalStore.update(s => {
      s.isSending = !isSending;
      s.receiver = INITIAL_RECEIVER_STATE;
      s.receiverError = "";
    });
  }, [onClick, isSending]);

  // This forces the AnimatedDropdown to open when the component unmounts and mounts again in this scenario:
  // Order Entry -> add receiver -> get quote -> Order Review -> click back button
  useEffect(() => {
    if (isSending) {
      forceOpen();
    }
  }, []);

  return (
    <div>
      <>
        <ReverseTokensButton isVisible={true} onClick={switchTradeTokens} />
        <div className="trade-box-header d-flex align-items-center justify-content-between">
          <MakerSectionHeader isSending={isSending} />
          <ToggleButton
            toggleButtonText={"Send to"}
            visible={wrapQuote === undefined}
            checked={isSending}
            handleOnToggle={handleToggle}
          />
        </div>
        <TokenSelectModal
          counterParty={"maker"}
          isOpen={modalOpen}
          onSelected={tokens => {
            GlobalStore.update(s => {
              s.makerTokens = tokens.map((token, index, arr) => {
                return {
                  token,
                  amount: Amount.ZERO,
                  pct: 100 / arr.length,
                  isLocked: false,
                };
              });
            });
          }}
          tokenSelectMode={tokenSelectMode}
          onClose={() => {
            setModalOpen(false);
          }}
        />
        {isDisplayed && <SwapAndSend animationState={animationState} />}
        <div data-testid="maker-trade-legs-section">
          {/* Show a trade leg if we don't have any tokens initially*/}
          {makerTokens.length === 0 ? (
            <TradeLeg
              key={"tokenSelect"}
              counterParty={"maker"}
              symbol={undefined}
              tokenSelectMode={tokenSelectMode}
            />
          ) : null}
          {/* Otherwise map through the existing list*/}
          {makerTokens.map(entry => {
            // If we have a single leg - then we replace the current token
            // when we have multiples - we allow for re-selecting
            const tokenSelectMode = makerTokens.length > 1 ? "multiple" : "single";
            return (
              <TradeLeg
                key={entry.token}
                counterParty={"maker"}
                symbol={entry ? entry.token : undefined}
                tokenSelectMode={tokenSelectMode}
              />
            );
          })}
          <div className={"maker-section-button-container d-flex justify-content-between"}>
            <SelectTokensButton
              visible={selectTokenButtonVisible}
              onClick={() => {
                setModalOpen(true);
              }}
            />
            <SplitEvenlyButton
              isVisible={makerTokens.length > 1}
              onClick={() => {
                GlobalStore.update(s => {
                  const roundedPercentages = getRoundedPcts();
                  s.makerTokens = s.makerTokens.map((entry, index) => {
                    entry.pct = roundedPercentages[index];
                    entry.isLocked = false;
                    return entry;
                  });
                });
              }}
              canSplit={
                !isEqual(
                  getRoundedPcts(),
                  makerTokens.map(e => e.pct)
                )
              }
            />
          </div>
        </div>
      </>
    </div>
  );
};
